export type PlatformCapacityProfileName = "prod" | "dev" | "local";

export interface PlatformCapacityPreset {
  profile: PlatformCapacityProfileName;
  maxConcurrentRunning: number | null;
  maxConcurrentWake: number | null;
  maxWakeQueueMs: number;
  minAvailableRamMB: number | null;
  evictionThresholdMB: number | null;
  sleepTimeoutMinutes: number;
  sleepMaxPerCycle: number;
  warmPoolMinIdle: number;
  warmPoolMax: number;
  deployCandidateReserve: number;
}

export const PLATFORM_CAPACITY_PROFILE_ENV = "GENCOW_CAPACITY_PROFILE";

export const PLATFORM_CAPACITY_ENV_KEYS = [
  PLATFORM_CAPACITY_PROFILE_ENV,
  "MAX_CONCURRENT_RUNNING",
  "MAX_CONCURRENT_WAKE",
  "MAX_WAKE_QUEUE_MS",
  "MIN_AVAILABLE_RAM_MB",
  "WAKE_RETRY_AFTER_SEC",
  "EVICTION_THRESHOLD_MB",
  "GENCOW_SLEEP_TIMEOUT_MINUTES",
  "GENCOW_SLEEP_MAX_PER_CYCLE",
  "WARM_POOL_MIN_IDLE",
  "WARM_POOL_MAX",
  "DEPLOY_CANDIDATE_RESERVE",
  "GENCOW_APP_PORT_RANGE",
  "COWBOX_WARM_POOL_PORT_RANGE",
] as const;

export const PLATFORM_CAPACITY_PRESETS: Record<PlatformCapacityProfileName, PlatformCapacityPreset> =
  Object.freeze({
    prod: Object.freeze({
      profile: "prod",
      maxConcurrentRunning: 600,
      maxConcurrentWake: 20,
      maxWakeQueueMs: 5000,
      minAvailableRamMB: 8192,
      evictionThresholdMB: 16384,
      sleepTimeoutMinutes: 15,
      sleepMaxPerCycle: 50,
      warmPoolMinIdle: 10,
      warmPoolMax: 650,
      deployCandidateReserve: 40,
    }),
    dev: Object.freeze({
      profile: "dev",
      maxConcurrentRunning: 60,
      maxConcurrentWake: 5,
      maxWakeQueueMs: 3000,
      minAvailableRamMB: 2048,
      evictionThresholdMB: 4096,
      sleepTimeoutMinutes: 10,
      sleepMaxPerCycle: 20,
      warmPoolMinIdle: 3,
      warmPoolMax: 70,
      deployCandidateReserve: 5,
    }),
    local: Object.freeze({
      profile: "local",
      maxConcurrentRunning: null,
      maxConcurrentWake: null,
      maxWakeQueueMs: 5000,
      minAvailableRamMB: null,
      evictionThresholdMB: null,
      sleepTimeoutMinutes: 30,
      sleepMaxPerCycle: 10,
      warmPoolMinIdle: 3,
      warmPoolMax: 10,
      deployCandidateReserve: 0,
    }),
  });

function normalizeDomain(value: string | undefined): string {
  return (value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "");
}

export function detectPlatformCapacityProfile(
  env: Record<string, string | undefined> = process.env,
): PlatformCapacityProfileName {
  const explicit = env[PLATFORM_CAPACITY_PROFILE_ENV]?.trim().toLowerCase();
  if (explicit) {
    if (["prod", "production", "128gb"].includes(explicit)) return "prod";
    if (["dev", "development", "16gb"].includes(explicit)) return "dev";
    if (["local", "test", "disabled"].includes(explicit)) return "local";
    throw new Error(`Invalid ${PLATFORM_CAPACITY_PROFILE_ENV}: ${explicit}. Expected prod, dev, or local.`);
  }

  const baseDomain = normalizeDomain(env.BASE_DOMAIN);
  if (baseDomain === "gencow.dev") return "dev";
  if (baseDomain === "gencow.app") return "prod";

  const platformUrl = normalizeDomain(env.GENCOW_PLATFORM_URL);
  if (platformUrl === "gencow.dev") return "dev";
  if (platformUrl === "gencow.app") return "prod";

  if (env.NODE_ENV === "production" && env.IS_PLATFORM === "true") return "prod";
  return "local";
}

export function resolvePlatformCapacityPreset(
  env: Record<string, string | undefined> = process.env,
): PlatformCapacityPreset {
  const profile = detectPlatformCapacityProfile(env);
  return { ...PLATFORM_CAPACITY_PRESETS[profile] };
}
